<template>
  <div class="kpis-table pt-4">
    <SearchBar
      v-if="showSearchBar"
      type="button"
      ghostText="Search KPI"
      :add="true"
      class="mb-5 mx-4"
      :label="appliedFilters.length > 0 ? 'More Filters' : 'Filter'"
      :length="appliedFilters.length"
      :chipText="chipTextDisplay"
      actionButtonIcon="add-icon.svg"
      btnText="Add KPI"
      @onClick="emitCorrectOnClick($event)"
      @onSearch="handleSearch($event)"
      @removeFilter="removeFilter"
      :valueSearch="searchText"
      :addOrAskIQButton="{
        enabled: true,
        label: 'Suggest',
        empLabel: 'Add KPI',
        showAIButton: false
      }"
    />
    <SearchModal
      ref="searchModal"
      :dialog="showFilterDialog"
      @back="showFilterDialog = false"
      @checkedItemsChanged="filterKPIs"
      :clearSelection="clearSelection"
      filterType="KPIs"
      :filterGroups="filterGroups"
      :groupsItems="groupsItems"
      :statusOptions="[]"
      :modalWidth="420"
      :defaultSelected="defaultSelected"
    />
    <DxDataGrid
      v-if="rows.length > 0"
      id="kpis-datagrid"
      :data-source="rows"
      :column-auto-width="false"
      :word-wrap-enabled="true"
      :show-row-lines="false"
      :show-column-lines="false"
      :show-borders="false"
      :element-attr="{ class: 'ex-kpis-table-grid' }"
      no-data-text=""
      height="auto"
    >
      <DxScrolling mode="standard" :use-native="true" show-scrollbar="never" />
      <DxPaging :enabled="false" />

      <!-- KPI Column -->
      <DxColumn
        data-field="name"
        caption="KPI"
        cell-template="kpiCell"
        header-cell-template="kpiHeader"
        :allowSearch="false"
        :allowSorting="false"
        :sortOrder="null"
      />
      <template #kpiHeader>
        <div class="d-flex align-center">
          <span>KPI</span>
        </div>
      </template>
      <template #kpiCell="{ data: options }">
        <div class="d-flex kpi-cell-wrapper">
          <span class="kpi-name ml-2">{{ options.data.name }}</span>
          <ProcedureVersionBadge
            class="procedure-version-badge-kpi"
            :version="options.data.version || 1"
          />
        </div>
      </template>

      <!-- Unit of Measure Column -->
      <DxColumn
        caption="Unit of Measure"
        cell-template="unitOfMeasureCell"
        header-cell-template="unitOfMeasureHeader"
        :allowSearch="false"
        :allowSorting="false"
        :width="200"
      />
      <template #unitOfMeasureHeader>
        <div class="d-flex justify-start full-width align-center">
          <span>Unit of Measure</span>
        </div>
      </template>
      <template #unitOfMeasureCell="{ data: options }">
        <div class="d-flex unit-of-measure-cell-wrapper align-center">
          <span class="unit-format">{{ options.data.unitFormat }}</span>
          <img
            src="@components/assets/decimal-precision.svg"
            alt="decimal"
            class="precision-picker__decimal-icon ml-1"
          />
          <span class="unit-precision ml-1">{{ options.data.precision }}</span>
        </div>
      </template>

      <!-- Favorability Column -->
      <DxColumn
        caption="Favorability"
        cell-template="favorabilityCell"
        header-cell-template="favorabilityHeader"
        :allowSearch="false"
        :allowSorting="false"
        :width="180"
      />
      <template #favorabilityHeader>
        <div class="d-flex justify-center full-width align-center">
          <span>Favorability</span>
        </div>
      </template>
      <template #favorabilityCell="{ data: options }">
        <div class="d-flex favorability-cell-wrapper">
          <FavorabilityIcon
            :less_is_better="options.data.lessIsBetter || false"
            :size="24"
            color="Grey"
          />
        </div>
      </template>

      <!-- Targets Column -->
      <DxColumn
        caption="Targets"
        cell-template="targetsCell"
        header-cell-template="targetsHeader"
        :allowSearch="false"
        :allowSorting="false"
        :width="180"
      />
      <template #targetsHeader>
        <div class="d-flex justify-center full-width align-center">
          <span>Targets</span>
        </div>
      </template>
      <template #targetsCell="{ data: options }">
        <div class="d-flex targets-cell-wrapper">
          <TargetsType
            :type="options.data.targetsType || 'Fixed'"
            :label="true"
          />
        </div>
      </template>

      <!-- Actual in Period is Column -->
      <DxColumn
        caption="Actual in Period is"
        cell-template="actualCell"
        header-cell-template="actualHeader"
        :allowSearch="false"
        :allowSorting="false"
        :width="180"
      />
      <template #actualHeader>
        <div class="d-flex justify-center full-width align-center">
          <span>Actual in Period is</span>
        </div>
      </template>
      <template #actualCell="{ data: options }">
        <div class="d-flex actual-cell-wrapper">
          <ActualCalculation
            :type="options.data.actualCalculation || 'Last'"
            :label="true"
          />
        </div>
      </template>

      <!-- Status Column -->
      <DxColumn
        caption="Status"
        cell-template="statusCell"
        header-cell-template="statusHeader"
        :allowSearch="false"
        :allowSorting="false"
        :width="180"
      />
      <template #statusHeader>
        <div class="d-flex justify-center full-width align-center">
          <span>Status</span>
        </div>
      </template>
      <template #statusCell="{ data: options }">
        <div class="d-flex status-cell-wrapper">
          <KPIStatusBadge :status="options.data.status || 'draft'" />
          <KPIPendingChangesBadge :hasPendingChanges="options.data.hasPendingChanges === true" />
        </div>
      </template>

      <!-- Chatter Column -->
      <DxColumn
        caption=""
        cell-template="chatterCell"
        :allowSearch="false"
        :allowSorting="false"
        :width="50"
      />
      <template #chatterCell="{ data: options }">
        <div class="chatter-clickable" @click="openChatter(options.data)">
          <ChatterIcon />
        </div>
      </template>

      <!-- Menu Column -->
      <DxColumn
        caption=""
        cell-template="menuCell"
        :allowSearch="false"
        :allowSorting="false"
        :width="50"
      />
      <template #menuCell="{ data: options }">
        <div class="hide-left-border center-vertical menu-cell-wrapper" @click.stop>
          <KPILineMenu
            :status="options.data.menuStatus"
            :hasPendingChanges="options.data.hasPendingChanges === true"
            :kpiId="options.data.id"
            :kpiName="options.data.name"
            :hiddenActions="hiddenMenuActions"
            @activate="onMenuAction('activate', options.data)"
            @delete="onMenuAction('delete', options.data)"
            @retire="onMenuAction('retire', options.data)"
            @reactivate="onMenuAction('reactivate', options.data)"
            @rename="onMenuAction('rename', options.data)"
            @clone="onMenuAction('clone', options.data)"
            @archive="onMenuAction('archive', options.data)"
            @unarchive="onMenuAction('unarchive', options.data)"
            @unarchive-and-rename="onMenuAction('unarchive-and-rename', options.data)"
          />
        </div>
      </template>
    </DxDataGrid>

    <!-- Pagination -->
    <ExPagination
      v-if="rows.length > 0"
      :pagination="pagination"
      @onRequest="handlePaginationRequest"
    />

    <!-- Empty State: No records exist at all (only show when no filters are applied) -->
    <NoRecordFound
      v-else-if="!showSearchBar"
      title="KPIs"
      message="KPIs"
      btnTitle="KPI"
      :showActiveCounter="false"
      :noBorder="true"
      showSpecificMessage="No KPIs exist in this catalog"
      description="There are no KPIs in this catalog"
      @onAction="$emit('add-kpi')"
      :shouldShowAddAction="true"
      :centeredMarginTop="'98px'"
      :contentWidth="'500px'"
    />

    <!-- Empty State: Records exist but filtered out -->
    <NoRecordFound
      v-else-if="showSearchBar"
      title="KPIs"
      message="KPIs"
      btnTitle="KPI"
      :shouldShowAddAction="false"
      :showActiveCounter="false"
      :noBorder="true"
      :showClearAction="true"
      :clearLabel="clearBtnLabel"
      :description="clearDescription"
      @onClear="clearSearch"
      :centeredMarginTop="'98px'"
      :contentWidth="'500px'"
    />
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import {
  DxDataGrid,
  DxColumn,
  DxScrolling,
  DxPaging,
} from "devextreme-vue/data-grid";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import ProcedureVersionBadge from "@components/ProcedureVersionBadge/ProcedureVersionBadge.vue";
import FavorabilityIcon from "@components/FavorabilityIcon/FavorabilityIcon.vue";
import TargetsType from "@components/TargetsType/TargetsType.vue";
import ActualCalculation from "@components/ActualCalculation/ActualCalculation.vue";
import KPIStatusBadge from "@components/KPIStatusBadge/KPIStatusBadge.vue";
import KPIPendingChangesBadge from "@components/KPIPendingChangesBadge/KPIPendingChangesBadge.vue";
import KPILineMenu from "@components/KPILineMenu/KPILineMenu.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";

const props = defineProps({
  // Rows of the current page, already filtered, sorted (name ascending) and paginated by the list story.
  kpis: {
    type: Array,
    default: () => [],
  },
  page: {
    type: Number,
    default: 1,
  },
  perPage: {
    type: Number,
    default: 25,
  },
  // Total rows matching the current search/filters; defaults to the rows given.
  totalCount: {
    type: Number,
    default: null,
  },
  initialSearchText: {
    type: String,
    default: "",
  },
  // Operating currencies ({ id, code }) offered by the Operating Currency filter; the group is hidden when empty.
  currencies: {
    type: Array,
    default: () => [],
  },
  // KPILineMenu actions the page has not wired yet (e.g. "clone"), left out of every row menu.
  hiddenMenuActions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "onButtonAction",
  "onSearch",
  "onFilter",
  "removeFilter",
  "checkedItemsChanged",
  "page-change",
  "add-kpi",
  "clear-search",
  "openChatter",
  "menu-action",
]);

const showFilterDialog = ref(false);
const clearSelection = ref("");
const searchModal = ref(null);

const DEFAULT_FILTERS = ["active", "draft"];

const searchText = ref(props.initialSearchText);
const appliedFilters = ref([...DEFAULT_FILTERS]);
const FILTER_NO_FILTER = "No Filter";

const radioItems = (group, labels) => [
  { label: FILTER_NO_FILTER, value: `no_filter_${group}` },
  ...labels.map((label) => ({ label, value: label.toLowerCase() })),
];

const statusGroup = {
  type: "check",
  label: "Status",
  items: [
    { label: "Draft", value: "draft" },
    { label: "Active", value: "active" },
    { label: "Retired", value: "retired" },
    { label: "Pending Changes", value: "pending-changes" },
    { label: "Archived", value: "archived" },
  ],
};
// MVP target types only: the backend TargetType enum has Fixed and Prorated (Rule 4).
const targetsTypeGroup = {
  type: "check",
  label: "Targets Type",
  items: [
    { label: "Fixed", value: "fixed" },
    { label: "Prorated", value: "prorated" },
  ],
};
const radioGroups = computed(() => [
  { type: "radio", label: "Format", items: radioItems("format", ["Currency", "Percent", "Number"]) },
  { type: "radio", label: "Favorability", items: radioItems("favorability", ["More", "Less"]) },
  ...(props.currencies.length > 0
    ? [
        {
          type: "radio",
          label: "Operating Currency",
          items: radioItems("currency", props.currencies.map((currency) => currency.code)),
        },
      ]
    : []),
]);
const groupsItems = computed(() => [
  { groups: [statusGroup, targetsTypeGroup, ...radioGroups.value] },
]);
const filterGroups = computed(() =>
  radioGroups.value.map(({ label, items }) => ({ label, items }))
);
const defaultSelected = ref([]);

// Chips show the status slug for checkbox filters and the chosen label for radio filters.
const chipTextDisplay = computed(() => appliedFilters.value);

const rows = computed(() =>
  props.kpis.map((kpi) => {
    const status = String(kpi.status || "draft").toLowerCase();
    return { ...kpi, menuStatus: status === "published" ? "active" : status };
  })
);

const pagination = computed(() => ({
  page: props.page,
  total: props.totalCount ?? props.kpis.length,
  perPage: props.perPage,
}));

const hasCustomFilters = computed(
  () =>
    appliedFilters.value.length !== DEFAULT_FILTERS.length ||
    !DEFAULT_FILTERS.every((filter) => appliedFilters.value.includes(filter))
);

const hasQuery = computed(() => searchText.value.length > 0 || hasCustomFilters.value);

const showSearchBar = computed(() => hasQuery.value || props.kpis.length > 0);

const clearBtnLabel = computed(() => {
  if (hasCustomFilters.value && searchText.value.length > 0) return "Clear All";
  if (hasCustomFilters.value) return "Clear Filters";
  if (searchText.value.length > 0) return "Clear Search";
  return "Clear";
});

const clearDescription = "Your search did not match any KPIs. Please try again.";

// Debounce utility function
let searchTimeout = null;
const debounce = (func, wait) => {
  return (...args) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    searchTimeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

const emitCorrectOnClick = (onClickType) => {
  if (onClickType === "add" || onClickType === "addIQAI") {
    emit("onButtonAction", onClickType);
  } else if (onClickType === "filter") {
    emit("onFilter");
    showFilterDialog.value = true;
  }
};

// Debounced search function with 300ms delay
const debouncedEmitSearch = debounce((searchValue) => {
  searchText.value = searchValue;
  emit("onSearch", searchValue);
}, 300);

const handleSearch = (searchValue) => {
  debouncedEmitSearch(searchValue);
};

const removeFilter = (removedFilter) => {
  clearSelection.value = removedFilter;
  appliedFilters.value = appliedFilters.value.filter((filter) => filter !== removedFilter);
  defaultSelected.value = defaultSelected.value.filter((filter) => filter !== removedFilter);
  emit("removeFilter", removedFilter);
};

// Checkbox items arrive as { label, value: slug }; radio items as { labelType, value: label }.
const filterKPIs = (filters) => {
  showFilterDialog.value = false;
  const chosen = filters
    .map((item) => (typeof item === "string" ? item : item?.value))
    .filter((value) => value && value !== FILTER_NO_FILTER);
  appliedFilters.value = chosen;
  // SearchModal re-checks radios by label and checkboxes by slug, both of which are the emitted values.
  defaultSelected.value = [...chosen];
  emit("checkedItemsChanged", filters);
};

const handlePaginationRequest = (paginationData) => {
  emit("page-change", paginationData.page);
};

const onMenuAction = (action, kpi) => {
  emit("menu-action", { action, kpi });
};

// Open chatter
const openChatter = (rowData) => {
  emit("openChatter", rowData);
};

// Clear search and filters
const clearSearch = () => {
  searchText.value = "";
  appliedFilters.value = [...DEFAULT_FILTERS];
  defaultSelected.value = [];
  clearSelection.value = "";
  emit("clear-search");
};

</script>

<style scoped>
.kpis-table {
  width: 100%;
  border-radius: 8px;
}

.kpis-table :deep(.dx-datagrid) {
  width: 100% !important;
}

.kpis-table :deep(.dx-datagrid-headers .dx-datagrid-table .dx-row > td) {
  background-color: #f9fafb;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  padding: 12px 24px;
  color: var(--colors-text-text-quaternary-500, #667085);
  border-top: 1px solid #eaecf0;
  border-bottom: 1px solid #eaecf0;
  vertical-align: middle !important;
}

.kpis-table :deep(.dx-datagrid .dx-column-lines > td) {
  border-left: transparent !important;
  border-right: transparent !important;
  border-bottom: 1px solid #eaecf0 !important;
}

.kpis-table :deep(.dx-datagrid-headers .dx-datagrid-table .dx-row > td),
.kpis-table :deep(.dx-datagrid .dx-row-lines > td) {
  border-bottom: 1px solid #eaecf0 !important;
}

.kpis-table :deep(.dx-datagrid-rowsview .dx-datagrid-table .dx-row > td) {
  border-bottom: 1px solid #eaecf0 !important;
}

.kpis-table :deep(.dx-datagrid-rowsview) {
  border-top: none;
}

.kpis-table :deep(td[role="columnheader"]) {
  vertical-align: middle !important;
  border-left: none !important;
  border-right: none !important;
}

.kpi-cell-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  width: 100%;
  padding: 8px 0;
  gap: 8px;
}

.kpi-name {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: var(--colors-text-text-secondary-700, #344054);
}

.unit-of-measure-cell-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #344054;
}

.unit-icon {
  flex-shrink: 0;
}

.unit-format {
  font-weight: 500;
}

.unit-metric {
  color: #667085;
}

.unit-precision {
  color: #667085;
}

.favorability-cell-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

.targets-cell-wrapper {
  display: flex;
  height: 100%;
  width: 100%;
}

.actual-cell-wrapper {
  display: flex;
  height: 100%;
  width: 100%;
}

.status-cell-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  height: 100%;
  width: 100%;
}

/* Chatter icon clickable styles */
.chatter-clickable {
  padding: 6px;
  text-align: center;
  margin: 5px;
  width: fit-content;
  cursor: url("../assets/cursorArrow.svg"), auto;
}

.hide-left-border {
  border-left: none !important;
}

.center-vertical {
  vertical-align: middle !important;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.kpis-table :deep(.dx-datagrid-content .dx-datagrid-table .dx-row > td),
.kpis-table :deep(.dx-datagrid-content .dx-datagrid-table .dx-row > tr > td) {
  vertical-align: middle !important;
  padding: 16px 24px;
}

.kpis-table
  :deep(.dx-datagrid-headers .dx-datagrid-table .dx-row > td:nth-child(-n + 2)),
.kpis-table
  :deep(.dx-datagrid-rowsview .dx-datagrid-table .dx-row > td:nth-child(-n + 2)) {
  padding-left: 12px;
  padding-right: 12px;
}

.kpis-table
  :deep(.dx-datagrid-headers .dx-datagrid-table .dx-row > td:nth-child(1)),
.kpis-table
  :deep(.dx-datagrid-headers .dx-datagrid-table .dx-row > td:nth-child(2)),
.kpis-table
  :deep(.dx-datagrid-headers .dx-datagrid-table .dx-row > td:nth-child(4)),
.kpis-table
  :deep(.dx-datagrid-headers .dx-datagrid-table .dx-row > td:nth-child(5)) {
  text-align: left !important;
}

.kpis-table
  :deep(.dx-datagrid-headers .dx-datagrid-table .dx-row > td:nth-child(3)),
.kpis-table
  :deep(.dx-datagrid-headers .dx-datagrid-table .dx-row > td:nth-child(6)) {
  text-align: center !important;
}

::v-deep(.search-add-btn) {
  width: auto !important;
  max-width: none !important;
  flex-shrink: 0 !important;
}

::v-deep(.filter-input-search) {
  width: 100% !important;
  max-width: 100% !important;
  float: none !important;
  flex: 1 1 auto !important;
}

::v-deep(.search-button) {
  align-items: flex-start !important;
}

::v-deep(.search-button .filter-input-search),
::v-deep(.search-button .search-add-btn),
::v-deep(.search-button .ml-2) {
  margin-top: 0 !important;
  align-self: flex-start !important;
}
::v-deep(.search-button .add-ask-or-iq-btn){
  margin-top: 4px !important;
}
::v-deep(.procedure-version-badge) {
  width: fit-content !important;
  padding: 2px 6px !important;
  background: #f9fafb !important;
  border: 1px solid #eaecf0 !important;
  border-radius: 8px !important;
  height: 29px !important;
  display: flex;
  justify-content: center;
  align-items: center;
}
.precision-picker__decimal-icon {
  width: 22px;
  height: 22px;
  aspect-ratio: 1/1;
}
::v-deep(.no-record-message) {
  position: relative !important;
  top: -20px !important;
}

/* Hide DevExtreme's default sort indicators */
.kpis-table :deep(.dx-datagrid-headers .dx-header-filter),
.kpis-table :deep(.dx-datagrid-headers .dx-sort-indicator),
.kpis-table :deep(.dx-datagrid-headers .dx-sort) {
  display: none !important;
}
</style>
